import { ButtonCus } from '@/components/common/Button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/common/Form';
import FormatNumberVer2 from '@/components/react-number-format/ReactNumberFormartver2';
import yup from '@/services/yup';
import { CancelBooking } from '@/types/system-setting';
import { currencyRawValue } from '@/utils/formatNumber';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface SettingCancelBookingProps {
    cancelBooking?: CancelBooking,
    onCancelBookingSubmit?: (cancelBooking: CancelBooking) => void
}

const SettingCancelBooking = ({ cancelBooking, onCancelBookingSubmit }: SettingCancelBookingProps) => {
    // hook
    const { t } = useTranslation();

    const validateSchema = yup.object().shape({
        periodOfTime1: yup
            .string()
            .trim()
            .checkInputOnlyPositiveDigitsOrEmpty(t('form_error_validate.only_number'))
            .required('form_error_validate.required'),
        periodOfTime2: yup
            .string()
            .trim()
            .checkInputOnlyPositiveDigitsOrEmpty(t('form_error_validate.only_number'))
            .required('form_error_validate.required')
            .test('is-greater', t('form_error_validate.max', { max: 100 }), function (value) {
                const periodOfTime2 = Number(value);
                return periodOfTime2 <= 100;
            }),
        periodOfTime3: yup
            .string()
            .trim()
            .checkInputOnlyPositiveDigitsOrEmpty(t('form_error_validate.only_number'))
            .required('form_error_validate.required')
            .test('is-greater', t('form_error_validate.max', { max: 100 }), function (value) {
                const periodOfTime3 = Number(value);
                return periodOfTime3 <= 100;
            })
    });

    type CancelBookingFormData = yup.InferType<typeof validateSchema>;

    const form = useForm<CancelBookingFormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: {
            periodOfTime1: '',
            periodOfTime2: '',
            periodOfTime3: ''
        }
    });

    // lifecycle
    useEffect(() => {
        form.reset({
            periodOfTime1: cancelBooking?.cancel72h?.toString(),
            periodOfTime2: cancelBooking?.cancelBefore72h?.toString(),
            periodOfTime3: cancelBooking?.cancelBefore48h?.toString()
        });
    }, [cancelBooking]);

    const onSubmit = async (data: CancelBookingFormData) => {
        const { periodOfTime1: periodOfTime1Str, periodOfTime2: periodOfTime2Str, periodOfTime3: periodOfTime3Str } = data;
        const periodOfTime1 = Number(currencyRawValue(periodOfTime1Str));
        const periodOfTime2 = Number(periodOfTime2Str);
        const periodOfTime3 = Number(periodOfTime3Str);
        const validData: CancelBooking = {};
        if (Number.isInteger(periodOfTime1) && periodOfTime1 >= 0) validData.cancel72h = periodOfTime1;
        if (Number.isInteger(periodOfTime2) && periodOfTime2 >= 0) validData.cancelBefore72h = periodOfTime2;
        if (Number.isInteger(periodOfTime3) && periodOfTime3 >= 0) validData.cancelBefore48h = periodOfTime3;
        if (onCancelBookingSubmit) {
            onCancelBookingSubmit(validData);
        }
    };

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className='flex flex-col gap-[30px]'>
                <div className='lg:w-1/2 xl:w-1/2'>
                    <FormField
                        control={form.control}
                        name='periodOfTime1'
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel required>{t('system_setting.setting_cancel_booking.cancel_booking_title.one', { 'hours': 72 })}</FormLabel>
                                <FormControl
                                    className='flex-shrink'
                                >
                                    <FormatNumberVer2
                                        decimalScale={0}
                                        statusDisplay='input'
                                        thousandSeparator={true}
                                        allowNegative={false}
                                        placeholder={t('placeholder.enter')}
                                        value={field.value}
                                        setvalueOfNumericFormat={(values) => {
                                            field.onChange(values);
                                        }}
                                        prefix={t('currency.currency_symbol')}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </div>
                <div className='lg:w-1/2 xl:w-1/2'>
                    <FormField
                        control={form.control}
                        name='periodOfTime2'
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel required>{t('system_setting.setting_cancel_booking.cancel_booking_title.two', { 'from': 48, 'to': 72 })}</FormLabel>
                                <FormControl className='flex-shrink'>
                                    <FormatNumberVer2
                                        decimalScale={0}
                                        statusDisplay='input'
                                        thousandSeparator={true}
                                        allowNegative={false}
                                        placeholder={t('placeholder.enter')}
                                        value={field.value}
                                        setvalueOfNumericFormat={(values) => {
                                            field.onChange(values);
                                        }}
                                        suffix='％'
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </div>
                <div className='lg:w-1/2 xl:w-1/2'>
                    <FormField
                        control={form.control}
                        name='periodOfTime3'
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel required>{t('system_setting.setting_cancel_booking.cancel_booking_title.two', { 'from': 0, 'to': 48 })}</FormLabel>
                                <FormControl className='flex-shrink'>
                                    <FormatNumberVer2
                                        decimalScale={0}
                                        statusDisplay='input'
                                        thousandSeparator={true}
                                        allowNegative={false}
                                        placeholder={t('placeholder.enter')}
                                        value={field.value}
                                        setvalueOfNumericFormat={(values) => {
                                            field.onChange(values);
                                        }}
                                        suffix='％'
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </div>
                <ButtonCus variant='default' size='sm' type='submit' className='self-center'>{t('button.save')}</ButtonCus>
            </form>
        </Form>
    );
};

export default SettingCancelBooking;
